import React from 'react';
import styled, { css } from 'styled-components';
import { editorButtonBaseStyles, keyboardFocusRing } from './EditorButton';
import { editorButtonTokens as tokens } from './editorButtonTokens';

export interface EditorButtonGroupItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

const raisedAboveNeighbours = (zIndex: number) => css`
  position: relative;
  z-index: ${zIndex};
`;

const StyledEditorButtonGroupItem = styled.button`
  ${editorButtonBaseStyles}
  width: ${tokens.height};
  padding: 0;
  background-color: ${tokens.buttonGroupBackground};
  color: ${tokens.primary};

  &:hover {
    color: ${tokens.primaryActive};
  }

  &:active {
    background-color: ${tokens.buttonGroupActive};
    color: ${tokens.primaryDeep};
  }

  &[aria-pressed='true'] {
    ${raisedAboveNeighbours(10)}
    background-color: ${tokens.buttonGroupActive};
    color: ${tokens.primaryDeep};
  }

  &:focus-visible {
    ${raisedAboveNeighbours(20)}
    background-color: ${tokens.buttonGroupActive};
    color: ${tokens.primaryDeep};
    ${keyboardFocusRing(tokens.primaryActive)}
  }

  &:disabled {
    background-color: ${tokens.secondaryDisabled};
    color: ${tokens.secondary};
  }
`;

const StyledEditorButtonGroup = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: ${tokens.height};

  > button,
  > * > button {
    border-radius: 0;
  }

  > button:first-child,
  > :first-child > button {
    border-top-left-radius: ${tokens.radius};
    border-bottom-left-radius: ${tokens.radius};
  }

  > button:last-child,
  > :last-child > button {
    border-top-right-radius: ${tokens.radius};
    border-bottom-right-radius: ${tokens.radius};
  }

  > button:not(:first-child),
  > :not(:first-child) > button {
    border-left: 2px solid ${tokens.buttonGroupSeparator};
  }
`;

export const EditorButtonGroup = (
  props: React.HTMLAttributes<HTMLDivElement>
) => <StyledEditorButtonGroup role="group" {...props} />;

export const EditorButtonGroupItem = ({
  active = false,
  type = 'button',
  ...props
}: EditorButtonGroupItemProps) => (
  <StyledEditorButtonGroupItem type={type} aria-pressed={active} {...props} />
);
